import React, { useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import {
	Button,
	Col,
	DatePicker,
	Form,
	Modal,
	Row,
	Select,
	Typography,
	notification,
} from "antd";
import { api } from "../utils/Api";
import {
	filterOption,
	getAvailableOptions,
	getDisabledDate,
	getDisabledTime,
	useShiftGroupOptions,
} from "./shiftGroupUtils";
const { Text } = Typography;

// Moves the member at memberIndex of group to another group of the same shift
const DriverShiftGroupMoveModal = ({
	open,
	onClose,
	onSuccess,
	shift,
	group,
	memberIndex,
	targetGroups = [],
	usedTrucks = [],
}) => {
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [apiNotification, contextHolder] = notification.useNotification();

	const member = group?.members[memberIndex];
	const shiftDate = shift ? dayjs(shift.rawDate) : null;

	const openNotification = (type, message, description) => {
		apiNotification[type]({
			message: message,
			description: description,
		});
	};

	// Only trucks are needed here, so no plant is passed to skip loading drivers
	const { truckOptions, loadingTrucks } = useShiftGroupOptions(
		open,
		null,
		openNotification,
	);

	const onFinish = async (values) => {
		const url =
			api + `/shift-group/member/${group._id}/${memberIndex}/move`;
		const token = localStorage.getItem("token");
		axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

		try {
			setLoading(true);
			const { data } = await axios.patch(url, {
				targetGroup: values.targetGroup,
				startDate: values.startDate.toDate(),
				endDate: values.endDate.toDate(),
				truck: values.truck,
			});
			if (data.success) {
				form.resetFields();
				onSuccess(data.message);
			} else {
				openNotification("error", "Error", data.message);
			}
		} catch (err) {
			console.error(err.message);
			openNotification("error", "Error", "Error while trying to move member");
		} finally {
			setLoading(false);
		}
	};

	return (
		<Modal
			title="Move Member"
			open={open}
			onCancel={onClose}
			centered
			width={1000}
			destroyOnClose
			maskClosable={false}
			footer={[
				<Button key="cancel" size="large" onClick={onClose} disabled={loading}>
					Cancel
				</Button>,
				<Button
					key="submit"
					type="primary"
					size="large"
					loading={loading}
					onClick={() => form.submit()}
				>
					Move
				</Button>,
			]}
		>
			{contextHolder}
			<Text style={{ display: "block" }}>
				<Text strong>Driver:</Text> {member?.driver?.name ?? ""}
			</Text>
			<Text style={{ display: "block", marginBottom: 16 }}>
				<Text strong>Current Group:</Text> {group?.name}
			</Text>
			<Form
				form={form}
				layout="vertical"
				onFinish={onFinish}
				preserve={false}
			>
				<Form.Item
					name="targetGroup"
					label="Move To Group"
					rules={[{ required: true, message: "Group is required" }]}
				>
					<Select
						showSearch
						placeholder="Select group"
						size="large"
						options={targetGroups.map((item) => ({
							label: item.name,
							value: item._id,
						}))}
						filterOption={filterOption}
					/>
				</Form.Item>
				<Row gutter={16}>
					<Col span={24} md={8}>
						<Form.Item
							name="startDate"
							label="Shift Start"
							initialValue={member ? dayjs(member.startDate) : undefined}
							rules={[
								{ required: true, message: "Shift start is required" },
								{
									validator: (_, value) =>
										!value || !shiftDate || !value.isBefore(shiftDate, "minute")
											? Promise.resolve()
											: Promise.reject(
													new Error(
														"Shift start can't be earlier than the shift date",
													),
											  ),
								},
							]}
						>
							<DatePicker
								size="large"
								showTime={{ format: "hh:mm A" }}
								format="MM/DD/YYYY hh:mm A"
								disabledDate={getDisabledDate(shiftDate)}
								disabledTime={getDisabledTime(shiftDate, false)}
								onChange={() =>
									form.validateFields(["endDate"]).catch(() => {})
								}
								style={{ width: "100%" }}
							/>
						</Form.Item>
					</Col>
					<Col span={24} md={8}>
						<Form.Item noStyle dependencies={["startDate"]}>
							{({ getFieldValue }) => {
								const startDate = getFieldValue("startDate");
								return (
									<Form.Item
										name="endDate"
										label="Shift End"
										initialValue={member ? dayjs(member.endDate) : undefined}
										rules={[
											{ required: true, message: "Shift end is required" },
											{
												validator: (_, value) =>
													!value || !startDate || value.isAfter(startDate, "minute")
														? Promise.resolve()
														: Promise.reject(
																new Error(
																	"Shift end must be later than shift start",
																),
														  ),
											},
										]}
									>
										<DatePicker
											size="large"
											showTime={{ format: "hh:mm A" }}
											format="MM/DD/YYYY hh:mm A"
											disabled={!startDate}
											disabledDate={getDisabledDate(startDate)}
											disabledTime={getDisabledTime(startDate, true)}
											style={{ width: "100%" }}
										/>
									</Form.Item>
								);
							}}
						</Form.Item>
					</Col>
					<Col span={24} md={8}>
						<Form.Item
							name="truck"
							label="Truck"
							initialValue={member?.truck?._id}
							rules={[{ required: true, message: "Truck is required" }]}
						>
							<Select
								showSearch
								placeholder="Select truck"
								size="large"
								options={getAvailableOptions(truckOptions, usedTrucks)}
								filterOption={filterOption}
								loading={loadingTrucks}
								disabled={loadingTrucks}
							/>
						</Form.Item>
					</Col>
				</Row>
			</Form>
		</Modal>
	);
};

export default DriverShiftGroupMoveModal;
